import { Progress } from "antd";
import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { FinalThinking } from "../FinalThinking";
import { SectionWithSlides } from "hooks/useStoryboardWithSlides/";
import { StreamingSlidePreview } from "pages/media/projects/components/slides/";
import type { Templates } from "pages/media/components/storyboardEditor/types/";
import { useBrandKit } from "hooks/useBrandKit/";
import { radius } from "@libertify/ui";

const PREVIEW_WIDTH = 280;

interface FinalVisualProps {
  isActive: boolean;
  delayMs: number;
  isPublished?: boolean;
  sections: SectionWithSlides[];
  templates: Templates;
  brandKitData: ReturnType<typeof useBrandKit>;
  mediaAspectRatio: number;
}

export const FinalVisual: React.FC<FinalVisualProps> = ({
  isActive,
  delayMs,
  isPublished,
  sections,
  templates,
  brandKitData,
  mediaAspectRatio,
}) => {
  const [activeScene, setActiveScene] = useState(0);
  const [progress, setProgress] = useState(0);

  const slides = useMemo(() => sections.flatMap((s) => s.slides), [sections]);

  const templateSize = useMemo(
    () => ({
      width: PREVIEW_WIDTH,
      height: Math.round(PREVIEW_WIDTH / mediaAspectRatio),
    }),
    [mediaAspectRatio]
  );

  useEffect(() => {
    if (!isActive || slides.length === 0) return;
    const iv = setInterval(
      () => setActiveScene((p) => (p + 1) % slides.length),
      1400
    );
    return () => clearInterval(iv);
  }, [isActive, slides.length]);

  useEffect(() => {
    if (!isActive) return;
    const start = Date.now();
    const iv = setInterval(() => {
      const pct = Math.min(100, ((Date.now() - start) / delayMs) * 100);
      setProgress(pct);
      if (pct >= 100) clearInterval(iv);
    }, 100);
    return () => clearInterval(iv);
  }, [isActive, delayMs]);

  return (
    <ContentIndent>
      <SlideLoopWrap style={templateSize}>
        {slides.map((slide, i) => {
          const template = templates.get(slide.uuid);
          if (!template) return null;
          return (
            <SlideOverlay key={slide.uuid} $active={i === activeScene}>
              <StreamingSlidePreview
                templateSize={templateSize}
                slide={slide}
                template={template}
                brandKitData={brandKitData}
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                onCardClick={() => {}}
                border={{ borderWidth: 0, borderRadius: 0 }}
                compact
              />
            </SlideOverlay>
          );
        })}
      </SlideLoopWrap>

      <Progress
        percent={isPublished ? 100 : Math.round(progress)}
        strokeColor="#14B8A6"
        trailColor="#F3F4F6"
        showInfo={false}
        strokeWidth={4}
        style={{ margin: "20px 0 8px" }}
      />

      <FinalThinking isActive={isActive} />
    </ContentIndent>
  );
};

const ContentIndent = styled.div`
  margin-left: 18px;
  margin-top: 25px;
`;

const SlideLoopWrap = styled.div`
  position: relative;
  border-radius: ${radius.box}px;
  overflow: hidden;
  background: #111827;
  margin: 0 auto 12px;
`;

const SlideOverlay = styled.div<{ $active: boolean }>`
  position: absolute;
  inset: 0;
  opacity: ${(p) => (p.$active ? 1 : 0)};
  transition: opacity 0.8s ease;
`;
